import React from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import Layout from '@components/Layout';
import useNavigationItems from '@config/navigationmenu';
import {
  Globe,
  BarChart3,
  FileCheck,
  Target,
  Users,
  Handshake,
  Languages,
  Building,
  Clock,
  Zap,
  CheckCircle
} from 'lucide-react';

export { default as Head } from '@components/PageHead';

const MarketEntrySupportPage = () => {
  const { t } = useTranslation("marketentry");
  const navigationItems = useNavigationItems();

  // Core Responsibilities
  const coreResponsibilities = [
    {
      icon: <BarChart3 size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item1.title"),
      description: t("coreResponsibilities.item1.description")
    },
    {
      icon: <FileCheck size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item2.title"),
      description: t("coreResponsibilities.item2.description")
    },
    {
      icon: <Target size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item3.title"),
      description: t("coreResponsibilities.item3.description")
    },
    {
      icon: <Handshake size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item4.title"),
      description: t("coreResponsibilities.item4.description")
    },
    {
      icon: <Users size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item5.title"),
      description: t("coreResponsibilities.item5.description")
    },
    {
      icon: <Languages size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item6.title"),
      description: t("coreResponsibilities.item6.description")
    },
    {
      icon: <Building size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item7.title"),
      description: t("coreResponsibilities.item7.description")
    },
    {
      icon: <Clock size={40} className="text-blue-600" />,
      title: t("coreResponsibilities.item8.title"),
      description: t("coreResponsibilities.item8.description")
    }
  ];

  // Why Choose Us
  const whyChooseUs = [
    {
      icon: <Globe size={40} className="text-blue-600" />,
      title: t("whyChooseUs.item1.title"),
      description: t("whyChooseUs.item1.description")
    },
    {
      icon: <CheckCircle size={40} className="text-blue-600" />,
      title: t("whyChooseUs.item2.title"),
      description: t("whyChooseUs.item2.description")
    },
    {
      icon: <Handshake size={40} className="text-blue-600" />,
      title: t("whyChooseUs.item3.title"),
      description: t("whyChooseUs.item3.description")
    },
    {
      icon: <Languages size={40} className="text-blue-600" />,
      title: t("whyChooseUs.item4.title"),
      description: t("whyChooseUs.item4.description")
    },
    {
      icon: <Zap size={40} className="text-blue-600" />,
      title: t("whyChooseUs.item5.title"),
      description: t("whyChooseUs.item5.description")
    }
  ];

  // Process Steps
  const processSteps = [
    {
      number: "1",
      title: t("process.step1.title"),
      description: t("process.step1.description")
    },
    {
      number: "2",
      title: t("process.step2.title"),
      description: t("process.step2.description")
    },
    {
      number: "3",
      title: t("process.step3.title"),
      description: t("process.step3.description")
    },
    {
      number: "4",
      title: t("process.step4.title"),
      description: t("process.step4.description")
    },
    {
      number: "5",
      title: t("process.step5.title"),
      description: t("process.step5.description")
    }
  ];

  return (
    <Layout headerProps={{ navigationItems }}>
      <div className="bg-white">

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-900 to-indigo-900 text-white py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t("hero.title")}
            </h1>
            <p className="text-xl text-blue-100 mb-8">{t("hero.description")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg font-semibold hover:from-blue-700 hover:to-cyan-700 transition-all">
                {t("hero.primaryCTA")}
              </button>
              <button className="px-8 py-4 border-2 border-white/30 rounded-lg font-semibold hover:bg-white/10 transition-all">
                {t("hero.secondaryCTA")}
              </button>
            </div>
          </div>
        </section>

        {/* Why Market Entry Matters */}
        <section className="py-20 bg-gray-50 text-center">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t("whyMarketEntryMatters.title")}
            </h2>
            <p className="text-xl text-gray-600 mb-6">{t("whyMarketEntryMatters.description")}</p>
            <div className="text-lg font-semibold text-blue-600">{t("whyMarketEntryMatters.statistic")}</div>
          </div>
        </section>

        {/* Core Responsibilities */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900">{t("coreResponsibilities.title")}</h2>
              <p className="text-xl text-gray-600">{t("coreResponsibilities.description")}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {coreResponsibilities.map((item, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900">{t("whyChooseUs.title")}</h2>
              <p className="text-xl text-gray-600">{t("whyChooseUs.description")}</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {whyChooseUs.map((item, index) => (
                <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900">{t("process.title")}</h2>
            </div>
            <div className="grid md:grid-cols-5 gap-8">
              {processSteps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                    {step.number}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-center">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-6">{t("cta.title")}</h2>
            <p className="text-xl text-blue-100 mb-8">{t("cta.description")}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors duration-200">
                {t("cta.primaryCTA")}
              </button>
              <button className="px-8 py-4 border-2 border-white/30 rounded-lg font-semibold hover:bg-white/10 transition-colors duration-200">
                {t("cta.secondaryCTA")}
              </button>
            </div>
          </div>
        </section>

      </div>
    </Layout>
  );
};

export default MarketEntrySupportPage;

export const query = graphql`
  query($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
